import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiArrowLeft, FiInfo, FiSidebar } from "react-icons/fi";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import { StatusBadge } from "../../../common/comms/StatusBadge";
import { TypingIndicator } from "../../../common/comms/TypingIndicator";
import { Toaster } from "../../../common/Toaster/Toaster";
import { useRoomMessages } from "./useRoomMessages";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { useViewerName } from "../../../hooks/comms/useViewerName";
import { ClientNameLink } from "../../../common/comms/ClientProfileProvider";
import { MessageBubble } from "./MessageBubble";
import { findSupersededEventCards } from "../../../utils/commsEventCard";
import { Composer } from "./Composer";
import { participantId, senderId } from "../../../utils/commsParticipantId";

/** Map an AI mode to a StatusBadge tone + label. */
const AI_MODE_BADGE = {
  ENABLED: { tone: "ai", label: "AI on" },
  DISABLED: { tone: "neutral", label: "AI off" },
  URGENCY_ONLY: { tone: "danger", label: "Urgency only" },
};

const SCROLL_TOP_THRESHOLD = 60;
const NEAR_BOTTOM_THRESHOLD = 120;

/** Format a date header label ("Today", "Yesterday", or a full date). */
const formatDateLabel = (iso) => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(date, today)) return "Today";
  if (sameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

/** Day key (YYYY-MM-DD) for grouping messages into date dividers. */
const dayKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

/**
 * The core chat thread for the Flex Comms Messages workspace.
 *
 * @param {Object} props
 * @param {string} props.roomId
 * @param {string} props.viewerUserId
 * @param {string} [props.viewerRole='COACH']
 * @param {boolean} [props.readOnly=false]
 * @param {'coral'|'blue'} [props.accentColor='coral']
 * @param {() => void} [props.onToggleRail]
 * @param {() => void} [props.onOpenDrawer]
 * @param {() => void} [props.onBack] - Optional back affordance (mobile inbox nav).
 * @param {{ text: string, nonce: number }|null} [props.composerPrefill] - Seeds
 *   the composer with draft text (e.g. a renewal check-in). The nonce forces a
 *   re-seed even when the same text is drafted twice.
 */
export function Thread({
  roomId,
  viewerUserId,
  viewerRole = "COACH",
  readOnly = false,
  accentColor = "coral",
  onToggleRail,
  onOpenDrawer,
  onBack,
  composerPrefill = null,
}) {
  const {
    messages,
    hasMore,
    loadingOlder,
    reads,
    participants,
    room,
    typingUsers,
    aiMode,
    canPublish,
    toast,
    send,
    loadOlder,
    markReadUpTo,
    deleteMessage,
    sendAiFeedback,
  } = useRoomMessages(roomId, viewerUserId);

  const { getClientName, getCoachName } = useCommsIdentity();
  const viewerName = useViewerName();

  /** Resolve a display name for a read-receipt id via the shared identity cache. */
  const resolveName = useCallback(
    (userId, role) => (role === "COACH" ? getCoachName(userId) : getClientName(userId)),
    [getClientName, getCoachName]
  );

  const [replyTo, setReplyTo] = useState(null);
  const listRef = useRef(null);
  const nearBottomRef = useRef(true);

  // --- Derived collections ---------------------------------------------------
  const coaches = useMemo(
    () =>
      participants.filter(
        (p) => p.participant_role === "COACH" && !p.removed_at
      ),
    [participants]
  );

  const activeCoaches = useMemo(
    () => coaches.filter((p) => p.can_write !== false),
    [coaches]
  );

  // The participants payload doesn't guarantee a `name`; without one the
  // composer's @-mention menu silently filters the coach out. Fill the gap
  // from the shared identity cache (getCoachName triggers its own refetch on
  // a miss and re-renders when names land).
  const mentionParticipants = useMemo(
    () =>
      participants.map((p) =>
        p.participant_role === "COACH" && !p.name
          ? { ...p, name: getCoachName(participantId(p)) }
          : p
      ),
    [participants, getCoachName]
  );

  const clientParticipant = useMemo(
    () => participants.find((p) => p.participant_role === "CLIENT"),
    [participants]
  );

  const clientName =
    clientParticipant?.name ||
    (room ? getClientName(room.client_id) : undefined) ||
    "Client";

  const messagesById = useMemo(() => {
    const map = new Map();
    for (const m of messages) {
      map.set(m.id, m);
    }
    return map;
  }, [messages]);

  const maxSeq = useMemo(
    () =>
      messages.reduce(
        (max, m) => (m._optimistic ? max : Math.max(max, m.seq || 0)),
        0
      ),
    [messages]
  );

  const lastOwnId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (senderId(messages[i]) === viewerUserId && !messages[i]._optimistic) {
        return messages[i].id;
      }
    }
    return null;
  }, [messages, viewerUserId]);

  const typingNames = useMemo(
    () => Object.values(typingUsers).filter(Boolean),
    [typingUsers]
  );

  // --- Read marking ----------------------------------------------------------
  useEffect(() => {
    if (!maxSeq) return;
    if (typeof document !== "undefined" && document.hidden) return;
    markReadUpTo(maxSeq);
  }, [maxSeq, markReadUpTo]);

  // --- Auto-scroll to bottom on new messages when already near bottom --------
  useEffect(() => {
    if (nearBottomRef.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length, typingNames.length]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    nearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD;
    if (el.scrollTop < SCROLL_TOP_THRESHOLD && hasMore && !loadingOlder) {
      const prevHeight = el.scrollHeight;
      loadOlder().then(() => {
        // Preserve scroll position after prepending older messages.
        if (listRef.current) {
          listRef.current.scrollTop =
            listRef.current.scrollHeight - prevHeight;
        }
      });
    }
  };

  const handleSubmit = async (payload) => {
    const finalPayload = replyTo
      ? { ...payload, reply_to_id: replyTo.id }
      : payload;
    await send(finalPayload);
    setReplyTo(null);
    nearBottomRef.current = true;
  };

  const status = room?.status;
  const isArchived = status === "archived";
  const isPaused = status === "paused";
  const badge = AI_MODE_BADGE[aiMode] || null;

  let bannerText = "";
  if (isArchived) {
    bannerText = "This coaching room has been archived.";
  } else if (isPaused) {
    bannerText =
      "Client subscription paused. They can read but not reply.";
  }

  // Event cards a newer card in this thread has replaced: dimmed at once,
  // without waiting for the server to stamp them (see findSupersededEventCards).
  const supersededCards = findSupersededEventCards(messages);

  // --- Render message list with date dividers --------------------------------
  const renderedMessages = [];
  let prevDay = null;
  for (const message of messages) {
    const key = dayKey(message.created_at);
    if (key !== prevDay) {
      renderedMessages.push(
        <div key={`divider-${key}`} className="flex justify-center my-4">
          <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-500 text-[11px] font-medium">
            {formatDateLabel(message.created_at)}
          </span>
        </div>
      );
      prevDay = key;
    }
    renderedMessages.push(
      <MessageBubble
        key={message.id}
        message={message}
        superseded={supersededCards.has(message.id)}
        viewerUserId={viewerUserId}
        viewerRole={viewerRole}
        readOnly={readOnly}
        participants={participants}
        reads={reads}
        activeCoaches={activeCoaches}
        messagesById={messagesById}
        accentColor={accentColor}
        isLastOwn={message.id === lastOwnId}
        resolveName={resolveName}
        viewerName={viewerName}
        onReply={setReplyTo}
        onDelete={deleteMessage}
        onAiFeedback={sendAiFeedback}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <header className="flex flex-shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-2 py-2 sm:gap-3 sm:px-4 sm:py-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to conversations"
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 sm:h-9 sm:w-9"
          >
            <FiArrowLeft size={18} />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold text-gray-900">
            <ClientNameLink clientId={room?.client_id} name={clientName} />
          </h2>
          {room?.display_id && (
            <p className="truncate text-[11px] text-gray-400">
              {room.display_id}
            </p>
          )}
        </div>

        {/* The coach stack is decorative context — on narrow screens the header
            budget goes to the client name and the actions instead. */}
        <div className="ml-2 hidden flex-shrink-0 items-center -space-x-2 sm:flex">
          {coaches.map((coach) => (
            <AvatarRing
              key={participantId(coach)}
              name={coach.name || getCoachName(participantId(coach)) || "Coach"}
              colorIndex={coach.coach_color_index ?? 0}
              size="sm"
            />
          ))}
          <AvatarRing isAI size="sm" />
        </div>

        {badge && (
          <div className="flex-shrink-0">
            <StatusBadge tone={badge.tone} dot>
              {badge.label}
            </StatusBadge>
          </div>
        )}

        <div className="flex flex-shrink-0 items-center gap-0.5 sm:gap-1">
          {onOpenDrawer && (
            <button
              type="button"
              onClick={onOpenDrawer}
              aria-label="Open info"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 sm:h-9 sm:w-9"
            >
              <FiInfo size={18} />
            </button>
          )}
          {onToggleRail && (
            <button
              type="button"
              onClick={onToggleRail}
              aria-label="Toggle rail"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 sm:h-9 sm:w-9"
            >
              <FiSidebar size={18} />
            </button>
          )}
        </div>
      </header>

      {/* Message list */}
      <div
        ref={listRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-2 py-2 sm:px-4"
      >
        <div className="mx-auto flex h-full w-full max-w-3xl flex-col">
          {hasMore && (
            <div className="flex justify-center py-2">
              <button
                type="button"
                onClick={loadOlder}
                disabled={loadingOlder}
                className="text-[11px] text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                {loadingOlder ? "Loading…" : "Load older messages"}
              </button>
            </div>
          )}

          {messages.length === 0 ? (
            <div className="flex flex-1 items-center justify-center text-sm text-gray-400">
              No messages yet.
            </div>
          ) : (
            <>
              {renderedMessages}

              {(typingNames.length > 0 || typingUsers.__ai__) && (
                <div className="px-2 py-1">
                  <TypingIndicator
                    names={typingNames}
                    isAI={Boolean(typingUsers.__ai__)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Composer / read-only bar */}
      {readOnly ? (
        <div className="px-4 py-3 border-t border-gray-200 bg-white text-center text-xs text-gray-400">
          Read-only
        </div>
      ) : (
        <Composer
          disabled={isArchived}
          canPublish={canPublish}
          participants={mentionParticipants}
          replyTo={replyTo}
          onClearReply={() => setReplyTo(null)}
          bannerText={bannerText}
          onSubmit={handleSubmit}
          initialText={composerPrefill?.text}
          initialTextNonce={composerPrefill?.nonce}
        />
      )}

      {toast && (
        <Toaster message={toast.message} type={toast.type} />
      )}
    </div>
  );
}
